import React, { useEffect, useState } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { Grid, IconButton, Tooltip } from '@material-ui/core';
import AddBoxIcon from '@material-ui/icons/AddBox';
import AlertDialog from '../../common/AlertDialog';
import styles from './externalAuthList.module.scss';
import EnhancedTable from '../../components/enhancedTable/enhancedTable';
import ExternalAuthModal from './ExternalAuthModal';
import { ExternalAuthConfig, ExternalAuthStatus } from './types';
import useExternalAuthColumns from './useExternalAuthColumns';

interface ExternalAuthListProps {
  orgId?: string;
}

const ExternalAuthList: React.FC<ExternalAuthListProps> = ({
  orgId: orgIdProp,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteItem, setDeleteItem] = useState<ExternalAuthConfig | null>(null);

  const storeOrgId = useStoreState(
    (state: any) => state.organization.organizationId,
  );
  const orgId: string = orgIdProp ?? storeOrgId;

  const externalAuthList: ExternalAuthConfig[] = useStoreState(
    (state: any) => state.externalAuth.externalAuthList,
  );
  const statusById: Record<string, ExternalAuthStatus> = useStoreState(
    (state: any) => state.externalAuth.statusById,
  );

  const externalAuthAction = useStoreActions(
    (actions: any) => actions.externalAuth,
  );
  const commonAction = useStoreActions((actions: any) => actions.common);

  useEffect(() => {
    if (!orgId) return;
    externalAuthAction.getExternalAuthList({ orgId });
    return () => {
      externalAuthAction.setCurrentExternalAuth(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const { columns, fileInputRef, handleFileChange } = useExternalAuthColumns({
    orgId,
    statusById,
    externalAuthAction,
    commonAction,
    onEdit: (item) => {
      setSelectedId(item.id ?? null);
      setShowModal(true);
    },
    onDeleteRequest: (item) => {
      setDeleteItem(item);
      setDeleteAlert(true);
    },
  });

  const deleteAlertDialogOpts = {
    title: `Delete - ${deleteItem?.name || ''}`,
    description: `Do you want to remove external auth "${
      deleteItem?.name || ''
    }"?`,
    btnOkText: 'Yes',
    btnCancelText: 'Cancel',
    onClose: (confirmed: boolean) => {
      if (confirmed && deleteItem?.id) {
        externalAuthAction.deleteExternalAuth({
          orgId,
          id: deleteItem.id,
          commonAction,
        });
      }
      setDeleteAlert(false);
      setDeleteItem(null);
    },
  };

  return (
    <div className={styles.root}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pem,.crt,.cer"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Grid item md={12}>
        <EnhancedTable
          columns={columns}
          data={externalAuthList}
          skipPageReset={false}
          paginationMode="client"
          pages={undefined}
          limit={undefined}
          currentPage={undefined}
          handleChangePage={undefined}
          handleChangeRowsPerPage={undefined}
          paginationDefauldSize={undefined}
          rowSelection={undefined}
          sortBy={undefined}
          paging={undefined}
          loader={undefined}
          toolbar={{
            show: true,
            title: 'External Auth',
            enableSearch: false,
            actionButtonGroup: () => (
              <Tooltip title="Add External Auth">
                <IconButton
                  color="primary"
                  aria-label="Add"
                  onClick={() => {
                    setSelectedId(null);
                    setShowModal(true);
                  }}
                >
                  <AddBoxIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
        />

        {showModal && (
          <ExternalAuthModal
            open={showModal}
            onClose={() => {
              setSelectedId(null);
              setShowModal(false);
            }}
            orgId={orgId}
            id={selectedId}
          />
        )}

        <AlertDialog opts={deleteAlertDialogOpts} open={deleteAlert} />
      </Grid>
    </div>
  );
};

export default ExternalAuthList;
